import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import AppLayout, { ProtectedRoute } from "./components/AppLayout";
import { useAuth, homeForRole } from "./context/AuthContext";

import Landing from "./pages/Landing";
import Privacy from "./pages/Privacy";
import Insights from "./pages/Insights";
import InsightArticle from "./pages/InsightArticle";
import PlatformBlog from "./pages/app/platform/PlatformBlog";
import PlatformBlogEditor from "./pages/app/platform/PlatformBlogEditor";
import PlatformEmail from "./pages/app/platform/PlatformEmail";
import ServicePoints from "./pages/app/ServicePoints";
import PublicPoint from "./pages/PublicPoint";
import PublicFeedback from "./pages/PublicFeedback";
import PublicInvite from "./pages/PublicInvite";
import VerifyCV from "./pages/VerifyCV";
import { Login, Register, ForgotPassword, ResetPassword } from "./pages/auth/AuthPages";
import AuthCallback from "./pages/auth/AuthCallback";

import CorporateDashboard from "./pages/app/CorporateDashboard";
import AiInsights from "./pages/app/AiInsights";
import AiChat from "./pages/app/AiChat";
import StoreDashboard from "./pages/app/StoreDashboard";
import Employees from "./pages/app/Employees";
import TeamAccess from "./pages/app/TeamAccess";
import EmployeeDetail from "./pages/app/EmployeeDetail";
import Locations from "./pages/app/Locations";
import Reports from "./pages/app/Reports";
import RatingQuestions from "./pages/app/RatingQuestions";
import FeedbackPage from "./pages/app/FeedbackPage";
import Reviews from "./pages/app/Reviews";
import Recovery from "./pages/app/Recovery";
import Disputes from "./pages/app/Disputes";
import Training from "./pages/app/Training";
import Merchandise from "./pages/app/Merchandise";
import Settings from "./pages/app/Settings";
import MyNotifications from "./pages/app/MyNotifications";
import OutletScorecard from "./pages/app/OutletScorecard";
import ExecutiveScorecard from "./pages/app/ExecutiveScorecard";
import Audit from "./pages/app/Audit";
import EmployeePortal from "./pages/app/EmployeePortal";
import Leaderboards from "./pages/app/Leaderboards";
import StoreKPI from "./pages/app/StoreKPI";
import StoreKPIDetail from "./pages/app/StoreKPIDetail";
import Rewards from "./pages/app/Rewards";
import CustomerVouchers from "./pages/app/CustomerVouchers";
import Benchmarking from "./pages/app/Benchmarking";
import Analytics from "./pages/app/Analytics";
import TrustCenter from "./pages/app/TrustCenter";
import PrintLanyard from "./pages/app/PrintLanyard";
import BulkPrint from "./pages/app/BulkPrint";
import PlatformOverview from "./pages/app/platform/PlatformOverview";
import PlatformLiveDashboard from "./pages/app/platform/PlatformLiveDashboard";
import PlatformEmails from "./pages/app/platform/PlatformEmails";
import PlatformAiUsage from "./pages/app/platform/PlatformAiUsage";
import PlatformOrganizations from "./pages/app/platform/PlatformOrganizations";
import PlatformSubscriptions from "./pages/app/platform/PlatformSubscriptions";
import PlatformStaff from "./pages/app/platform/PlatformStaff";
import PlatformReports from "./pages/app/platform/PlatformReports";
import PlatformMerchandise from "./pages/app/platform/PlatformMerchandise";
import PlatformOperations from "./pages/app/platform/PlatformOperations";
import PlatformFinance from "./pages/app/platform/PlatformFinance";
import PlatformBilling from "./pages/app/platform/PlatformBilling";
import PlatformSystemReport from "./pages/app/platform/PlatformSystemReport";
import Billing from "./pages/app/Billing";

const CORP = "corporate_admin";
const STORE = "store_admin";
const EMP = "employee";
const ADMINS = [CORP, STORE];
const MASTER = "master_admin";
const SALES = "platform_sales";
const OPS = "platform_ops";
const FINANCE = "platform_finance";
const PLATFORM = [MASTER, SALES, OPS, FINANCE];

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

function RoleShell({ roles, children }) {
  return (
    <ProtectedRoute roles={roles}>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

function AppIndexRedirect() {
  const { user } = useAuth();
  return <Navigate to={homeForRole(user?.role)} replace />;
}

// [path, Component] — public pages, no auth.
const PUBLIC_ROUTES = [
  ["/", Landing], ["/privacy", Privacy], ["/insights", Insights], ["/insights/:slug", InsightArticle],
  ["/r/:token", PublicFeedback], ["/p/:token", PublicPoint], ["/verify/:code", VerifyCV], ["/invite/:code", PublicInvite],
  ["/login", Login], ["/register", Register], ["/forgot-password", ForgotPassword], ["/reset-password", ResetPassword],
];

// [path, roles (null = any signed-in user), Component] — rendered inside the app shell.
const APP_ROUTES = [
  ["/app", null, AppIndexRedirect],
  ["/app/corporate", [CORP], CorporateDashboard], ["/app/ai", ADMINS, AiInsights], ["/app/ai/chat", ADMINS, AiChat],
  ["/app/store", ADMINS, StoreDashboard], ["/app/employees", ADMINS, Employees], ["/app/counters-tables", ADMINS, ServicePoints],
  ["/app/team", ADMINS, TeamAccess], ["/app/employees/:id", ADMINS, EmployeeDetail], ["/app/locations", [CORP], Locations],
  ["/app/reports", ADMINS, Reports], ["/app/questions", ADMINS, RatingQuestions], ["/app/feedback", ADMINS, FeedbackPage],
  ["/app/reviews", ADMINS, Reviews],
  ["/app/recovery", ADMINS, Recovery], ["/app/disputes", null, Disputes], ["/app/training", null, Training],
  ["/app/merchandise", ADMINS, Merchandise], ["/app/leaderboards", null, Leaderboards], ["/app/store-kpi", ADMINS, StoreKPI], ["/app/store-kpi/:locId", ADMINS, StoreKPIDetail], ["/app/rewards", ADMINS, Rewards],
  ["/app/vouchers", ADMINS, CustomerVouchers],
  ["/app/benchmarking", [CORP], Benchmarking], ["/app/billing", ADMINS, Billing], ["/app/analytics", ADMINS, Analytics],
  ["/app/scorecards/outlet", ADMINS, OutletScorecard], ["/app/scorecards/executive", [CORP], ExecutiveScorecard],
  ["/app/trust", ADMINS, TrustCenter], ["/app/settings", [CORP], Settings], ["/app/my-notifications", ADMINS, MyNotifications],
  ["/app/audit", [CORP], Audit], ["/app/me", [EMP], EmployeePortal],
  ["/app/platform", [MASTER], PlatformOverview], ["/app/platform/live", [MASTER], PlatformLiveDashboard], ["/app/platform/emails", [MASTER], PlatformEmails], ["/app/platform/ai-usage", [MASTER], PlatformAiUsage], ["/app/platform/organizations", PLATFORM, PlatformOrganizations],
  ["/app/platform/subscriptions", [MASTER, SALES, FINANCE], PlatformSubscriptions], ["/app/platform/staff", [MASTER], PlatformStaff],
  ["/app/platform/reports", [MASTER, FINANCE], PlatformReports], ["/app/platform/merchandise", PLATFORM, PlatformMerchandise],
  ["/app/platform/operations", [MASTER, OPS], PlatformOperations], ["/app/platform/finance", [MASTER, FINANCE], PlatformFinance],
  ["/app/platform/billing", [MASTER, FINANCE], PlatformBilling], ["/app/platform/system-report", [MASTER], PlatformSystemReport],
  ["/app/platform/email", [MASTER], PlatformEmail], ["/app/platform/blog", [MASTER, OPS, FINANCE], PlatformBlog],
  ["/app/platform/blog/:id", [MASTER, OPS, FINANCE], PlatformBlogEditor],
];

// Full-page print views: auth-protected but without the app shell.
const PRINT_ROUTES = [["/app/employees/:id/print", PrintLanyard], ["/app/print/location/:locId", BulkPrint]];

const appElement = (roles, Page) => (roles ? <RoleShell roles={roles}><Page /></RoleShell> : <Shell><Page /></Shell>);

function Root() {
  const location = useLocation();
  // SPA page_view ping to Google Ads on every route change
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {PUBLIC_ROUTES.map(([path, Page]) => <Route key={path} path={path} element={<Page />} />)}
      {APP_ROUTES.map(([path, roles, Page]) => <Route key={path} path={path} element={appElement(roles, Page)} />)}
      {PRINT_ROUTES.map(([path, Page]) => <Route key={path} path={path} element={<ProtectedRoute roles={ADMINS}><Page /></ProtectedRoute>} />)}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Root />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
